import {
  VACANCY_STATUS_OPTIONS,
  EMPLOYEE_CONTRACT_TYPES,
  POSITION_TYPE_OPTIONS,
  COMPENSATION_CURRENCY_TYPES,
} from './constants';

const VACANCY_PROPERTY_LABELS: Record<string, string> = {
  title: 'Job title',
  status: 'Status',
  isAsap: 'ASAP hiring',
  targetHiringDate: 'Target hiring date',
  hiringMode: 'Hiring mode',
  responsibilities: 'Responsibilities',
  vacancyGroup: 'Vacancy group',
  areAllHired: 'All hired',
  responsiblePerson: 'Responsible person',
  cancelReason: 'Cancellation reason',
  salaryFromId: 'Salary from',
  salaryToId: 'Salary to',
  skills: 'Skills',
  createdAt: 'Created date',
  updatedAt: 'Updated date',
};

const WORKING_CONDITIONS_PROPERTY_LABELS: Record<string, string> = {
  registration: 'Registration country',
  accommodation: 'Work location',
  employmentTypes: 'Employment type',
  contractTypes: 'Contract type',
  isRelocationAvailable: 'Relocation available',
  compensation: 'Compensation',
};

const COMPENSATION_PROPERTY_LABELS: Record<string, string> = {
  salaryFromId: 'Salary from',
  salaryToId: 'Salary to',
  currency: 'Currency',
};

const POSITION_PROPERTY_LABELS: Record<string, string> = {
  type: 'Position type',
  status: 'Position status',
  isCandidateRejected: 'Candidate rejected',
  forecastDate: 'Forecast date',
  openDate: 'Open date',
  closeDate: 'Close date',
  startWorkingDate: 'Start working date',
  id: 'ID',
  applicationId: 'Application ID',
};

const NOTES_PROPERTY_LABELS: Record<string, string> = {
  description: 'Description',
  attachmentIds: 'Attachments',
};

const MANDATORY_REQUIREMENTS_PROPERTY_LABELS: Record<string, string> = {
  description: 'Description',
  skills: 'Skills',
  requiredExperienceInYears: 'Required experience in years',
  englishLevel: 'English level',
};

const OPTIONAL_REQUIREMENTS_PROPERTY_LABELS: Record<string, string> = {
  description: 'Description',
  skills: 'Skills',
};

const PROJECT_PROPERTY_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
};

export const DOMAIN_LABELS: Record<string, string> = {
  Vacancy: 'Vacancy',
  WorkConditions: 'Working conditions',
  Compensation: 'Compensation',
  Position: 'Position',
  MandatoryRequirements: 'Mandatory requirements',
  OptionalRequirements: 'Optional requirements',
  Notes: 'Notes',
  Projects: 'Projects',
};

export const FORMAT_ENUM_VALUES: Record<string, string> = {
  REQUIRES_REVIEW: VACANCY_STATUS_OPTIONS.REQUIRES_REVIEW,
  OPEN: VACANCY_STATUS_OPTIONS.OPEN,
  CANCELLATION_REQUESTED: VACANCY_STATUS_OPTIONS.CANCELLATION_REQUESTED,
  CANCELLED: VACANCY_STATUS_OPTIONS.CANCELLED,
  CLOSED: VACANCY_STATUS_OPTIONS.CLOSED,
  EXPIRED: VACANCY_STATUS_OPTIONS.EXPIRED,

  ACTIVE: 'Active',
  BAR_RAISER: 'Bar Raiser',

  BY: 'Belarus',
  PL: 'Poland',
  US: 'United States',

  EU: 'European Union',
  ANYWHERE: 'Remote (Anywhere)',

  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',

  EMPLOYMENT_CONTRACT: EMPLOYEE_CONTRACT_TYPES.EMPLOYMENT_CONTRACT,
  B2B_CONTRACT: EMPLOYEE_CONTRACT_TYPES.B2B_CONTRACT,
  CONTRACT_FOR_SERVICES: EMPLOYEE_CONTRACT_TYPES.CONTRACT_FOR_SERVICES,
  INTERNSHIP: 'Internship',

  REQUIRED: POSITION_TYPE_OPTIONS.REQUIRED,
  ADDITIONAL: POSITION_TYPE_OPTIONS.ADDITIONAL,

  HIRED: 'Hired',

  USD: COMPENSATION_CURRENCY_TYPES.USD,
  BYN: COMPENSATION_CURRENCY_TYPES.BYN,
  PLN: COMPENSATION_CURRENCY_TYPES.PLN,
  EUR: 'EUR',
};

const formatBoolean = (value: boolean | string | null | undefined): string => {
  if (value === null || value === undefined) return 'Not specified';

  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim();
    if (lowerValue === 'true') return 'Yes';
    if (lowerValue === 'false') return 'No';

    try {
      const parsedValue = JSON.parse(lowerValue);
      if (typeof parsedValue === 'boolean') {
        return parsedValue ? 'Yes' : 'No';
      }
    } catch (e) {
      return value;
    }

    return value;
  }

  return value ? 'Yes' : 'No';
};

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Not specified';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (e) {
    return dateString;
  }
};

const formatArray = (array: string[] | string): string => {
  if (typeof array === 'string') {
    if (array === '[]' || array === '') return 'None';

    try {
      const parsedArray = JSON.parse(array);
      if (Array.isArray(parsedArray)) {
        if (parsedArray.length === 0) return 'None';
        return parsedArray.map(item => FORMAT_ENUM_VALUES[item] || item).join(', ');
      }
    } catch (e) {
      return array;
    }

    return array;
  }

  if (array.length === 0) return 'None';
  return array.map(item => FORMAT_ENUM_VALUES[item] || item).join(', ');
};

export const formatPropertyValue = (domain: string, property: string, value: any): string => {
  if (value === null || value === undefined || value === '') {
    return 'Not specified';
  }

  if (typeof value === 'boolean') {
    return formatBoolean(value);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (trimmedValue === 'true' || trimmedValue === 'false') {
      return formatBoolean(trimmedValue);
    }

    if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
      return formatArray(trimmedValue);
    }
  }

  if (Array.isArray(value)) {
    return formatArray(value);
  }

  const isDateProperty = /(date|at)$/i.test(property);
  if (isDateProperty && typeof value === 'string') {
    return formatDate(value);
  }

  return FORMAT_ENUM_VALUES[value] || String(value);
};

export const getPropertyLabel = (domain: string, property: string): string => {
  const domainKey = domain.replace(/s$/, '');

  if (/^Position\s*\d*$/.test(domainKey)) {
    return POSITION_PROPERTY_LABELS[property] || property;
  }
  if (/^Working Condition\s*\d*$/.test(domainKey)) {
    return WORKING_CONDITIONS_PROPERTY_LABELS[property] || property;
  }
  switch (domainKey) {
    case 'Vacancy':
      return VACANCY_PROPERTY_LABELS[property] || property;
    case 'Compensation':
      return COMPENSATION_PROPERTY_LABELS[property] || property;
    case 'Note':
      return NOTES_PROPERTY_LABELS[property] || property;
    case 'Mandatory Requirement':
      return MANDATORY_REQUIREMENTS_PROPERTY_LABELS[property] || property;
    case 'Optional Requirement':
      return OPTIONAL_REQUIREMENTS_PROPERTY_LABELS[property] || property;
    case 'Project':
      return PROJECT_PROPERTY_LABELS[property] || property;
    default:
      return property;
  }
};
